import React, { Component, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BugReportIcon from '@mui/icons-material/BugReport';
import { getAppInsights } from '../../Services/Global';
import logger from '../../Services/LoggingService';

interface ErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  errorInfo?: string;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  errorId: string;
}

interface GlobalErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

// Functional component for error display to use theme
const ErrorDisplay: React.FC<{
  error: Error;
  errorInfo: ErrorInfo;
  errorId: string;
  showDetails: boolean;
  onToggleDetails: () => void;
  onRetry: () => void;
  onReload: () => void;
}> = ({ error, errorInfo, errorId, showDetails, onToggleDetails, onRetry, onReload }) => {
  const theme = useTheme();

  const styles = {
    container: {
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(3)
    },
    errorPaper: {
      maxWidth: 800,
      width: '100%',
      padding: theme.spacing(4),
      textAlign: 'center' as const
    },
    errorIcon: {
      fontSize: 64,
      color: theme.palette.error.main,
      marginBottom: theme.spacing(2)
    },
    actionButtons: {
      marginTop: theme.spacing(3),
      gap: theme.spacing(2)
    },
    detailsSection: {
      marginTop: theme.spacing(3),
      textAlign: 'left' as const
    },
    codeBlock: {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      maxHeight: 200,
      overflow: 'auto',
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const
    },
    errorChip: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper elevation={3} sx={styles.errorPaper}>
        <BugReportIcon sx={styles.errorIcon} />
        
        <Typography variant="h4" component="h1" gutterBottom color="error">
          Something went wrong
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          An unexpected error occurred in the application. This has been logged and 
          our team has been notified. Please try refreshing the page or contact support 
          if the problem persists.
        </Typography>

        <Chip 
          label={`Error ID: ${errorId}`}
          variant="outlined"
          size="small"
          sx={styles.errorChip}
        />

        <Stack direction="row" spacing={2} justifyContent="center" sx={styles.actionButtons}>
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            startIcon={<RefreshIcon />}
          >
            Try Again
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            onClick={onReload}
          >
            Reload Page
          </Button>

          <Button
            variant="text"
            color="primary"
            onClick={onToggleDetails}
            startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </Stack>

        {showDetails && (
          <Box sx={styles.detailsSection}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Error Details</AlertTitle>
              <Typography variant="body2" component="div">
                <strong>Error:</strong> {error.name}: {error.message}
              </Typography>
            </Alert>

            {error.stack && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Stack Trace:
                </Typography>
                <Box sx={styles.codeBlock}>
                  {error.stack}
                </Box>
              </Box>
            )}

            {errorInfo.componentStack && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Component Stack:
                </Typography>
                <Box sx={styles.codeBlock}>
                  {errorInfo.componentStack}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

class GlobalErrorBoundary extends Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<GlobalErrorBoundaryState> {
    // Generate unique error ID
    const errorId = `GLB-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId;
    
    // Log to console for development
    logger.error('Global Error Boundary caught an error', error, {
      errorInfo: errorInfo.componentStack,
      errorId
    }, {
      component: 'GlobalErrorBoundary',
      method: 'componentDidCatch'
    });

    // Log to Application Insights
    try {
      const appInsights = getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error,
          properties: {
            errorBoundary: 'GlobalErrorBoundary',
            errorId: errorId,
            componentStack: errorInfo.componentStack || '',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href || 'unknown',
            userId: 'unknown'
          }
        });
        
        // Track event with proper structure
        appInsights.trackEvent({
          name: 'ErrorBoundary:GlobalError',
          properties: {
            errorId: errorId,
            errorName: error.name,
            errorMessage: error.message,
            boundary: 'Global',
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (loggingError) {
      logger.error('Failed to log error to Application Insights', loggingError as Error, null, {
        component: 'GlobalErrorBoundary',
        method: 'componentDidCatch'
      });
    }

    this.setState({
      errorInfo: {
        componentStack: errorInfo.componentStack || '',
        errorBoundary: 'GlobalErrorBoundary'
      }
    });
  }

  handleRetry = (): void => {
    // Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: ''
    });
  };

  handleReload = (): void => {
    // Force page reload
    window.location.reload();
  };

  handleToggleDetails = (): void => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      // Show custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Show default error UI
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          showDetails={this.state.showDetails}
          onToggleDetails={this.handleToggleDetails}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary; 